/**
 * CRM module — company lookup & VAT verification (user steer 2026-07-13,
 * "fully configurable from the CRM module settings").
 *
 * Two seams, both provider-routed off `solum.crm.company` settings:
 *  · VAT check — `vies` (EU REST API, qualified check when a requester VAT is
 *    configured → the consultation number IS the reverse-charge audit
 *    evidence, store it) · `builtin` (offline format + checksum validation,
 *    deterministic for demos/e2e) · off. VIES unreachable ⇒ falls back to the
 *    format check and says so — never hard-block a save on VIES uptime.
 *  · Company search — per-registry toggles: `builtin` (offline demo book) ·
 *    Brønnøysund NO (keyless, CORS-friendly) · recherche-entreprises FR
 *    (keyless) · Companies House UK (key; needs the server-side worker — the
 *    key never ships to a browser, so the client keeps it honest and inert).
 *
 * In production these settings are crm-module `core.config` rows and the
 * keyed/CORS-blocked providers run through an edge worker.
 */
import { useSyncExternalStore } from 'react'
/* ── #524: these are SETTINGS, not browser storage ─────────────────────────
 * This module used to persist to `localStorage`, which meant the provider API
 * keys were per-BROWSER, plaintext, un-audited, admin-gated by nothing, and
 * invisible to `core.describe_settings()` — so `is_secret` (masking,
 * admin-gating) never applied to them. Same class as #267: `is_secret` protects
 * what is REGISTERED, so anything unregistered is protected by nothing.
 *
 * They are now declared in `crm/2026080402` and live in `core.setting`. The
 * localStorage path is DELETED rather than kept as a fallback — a fallback that
 * still writes to the browser is the defect with a longer name.
 *
 * The store stays synchronous (`getX()` is called from phone normalisation, a
 * hot path with no place to await), so it is a module-level cache HYDRATED once
 * at startup by `hydrateCrmSettings`, exactly as BrandSync hydrates the brand.
 * Before hydration the DEFAULTS apply, which is the honest answer: the offline
 * built-in providers, and no keys.
 */

export interface CompanySettings {
  vatProvider: 'builtin' | 'vies' | 'none'
  /** your own VAT number — turns VIES checks into QUALIFIED checks (consultation number) */
  vatRequester: string
  /** four INDEPENDENT switches — four declared booleans, not one json blob (crm
   *  2026080422: the generic Settings surface has no json editor, so editing the
   *  blob stored a string and every registry silently read `undefined`) */
  registries: { builtin: boolean; brreg: boolean; fr: boolean; uk: boolean }
  /** default region for phone parsing/normalization (ISO 3166-1 alpha-2) */
  phoneCountry: string
}
/* No `ukKey` here, deliberately. The Companies House key stays a governed secret
 * (`crm.uk_key`) and is read SERVER-SIDE; the console never called that registry
 * and has no business caching a value `describe_settings` only ever masks. */

const DEFAULTS: CompanySettings = {
  vatProvider: 'builtin', vatRequester: '',
  registries: { builtin: true, brreg: true, fr: true, uk: false },
  phoneCountry: 'BE',
}

let state: CompanySettings = DEFAULTS
const subs = new Set<() => void>()

/** Cache-only. Used by the startup hydration; does NOT write to the server. */
export function applyCompanySettings(patch: Partial<CompanySettings>) {
  state = { ...state, ...patch, registries: { ...state.registries, ...patch.registries } }
  subs.forEach((f) => f())
}
export function useCompanySettings(): CompanySettings {
  return useSyncExternalStore((cb) => { subs.add(cb); return () => { subs.delete(cb) } }, () => state)
}
export const getCompanySettings = () => state

/* ── VAT verification ── */
export interface VatResult {
  status: 'valid' | 'invalid' | 'format_valid' | 'format_invalid' | 'unavailable'
  provider: string
  checked_at: string
  name?: string
  consultation?: string
  note?: string
}

/** Offline format rules (checksum where cheap) — the floor every country gets. */
const VAT_FORMATS: Record<string, RegExp> = {
  BE: /^[01]\d{9}$/, NL: /^\d{9}B\d{2}$/, DE: /^\d{9}$/, FR: /^[A-Z0-9]{2}\d{9}$/,
  LU: /^\d{8}$/, GB: /^\d{9}(\d{3})?$/, IT: /^\d{11}$/, ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
  AT: /^U\d{8}$/, DK: /^\d{8}$/, SE: /^\d{12}$/, PL: /^\d{10}$/, PT: /^\d{9}$/,
  IE: /^\d{7}[A-Z]{1,2}$|^\d[A-Z0-9]\d{5}[A-Z]$/, FI: /^\d{8}$/, NO: /^\d{9}(MVA)?$/, CH: /^E?\d{9}(MWST|TVA|IVA)?$/,
}
const beChecksum = (digits: string) => Number(digits.slice(8)) === 97 - (Number(digits.slice(0, 8)) % 97)

export function checkVatFormat(country: string, num: string): VatResult {
  const cc = country.toUpperCase()
  const digits = num.replace(/^[A-Z]{2}/i, '').replace(/[\s.]/g, '').toUpperCase()
  const now = new Date().toISOString()
  const re = VAT_FORMATS[cc]
  if (!re) return { status: 'format_valid', provider: 'builtin', checked_at: now, note: `no format rule for ${cc} — not validated` }
  const formatOk = re.test(digits) && (cc !== 'BE' || beChecksum(digits))
  return { status: formatOk ? 'format_valid' : 'format_invalid', provider: 'builtin', checked_at: now }
}

interface ViesResponse { isValid?: boolean; name?: string; requestIdentifier?: string; userError?: string }

export async function checkVat(country: string, num: string, s: CompanySettings = state): Promise<VatResult> {
  if (s.vatProvider === 'none') return { status: 'unavailable', provider: 'off', checked_at: new Date().toISOString(), note: 'VAT checks are switched off' }
  const cc = country.toUpperCase()
  const digits = num.replace(/^[A-Z]{2}/i, '').replace(/[\s.]/g, '').toUpperCase()
  if (s.vatProvider === 'vies') {
    try {
      const req = s.vatRequester.trim()
      const qualified = req.length > 4
        ? `?requesterMemberStateCode=${encodeURIComponent(req.slice(0, 2).toUpperCase())}&requesterNumber=${encodeURIComponent(req.slice(2).replace(/[\s.]/g, ''))}`
        : ''
      const res = await fetch(`https://ec.europa.eu/taxation_customs/vies/rest-api/ms/${cc}/vat/${digits}${qualified}`, { headers: { Accept: 'application/json' } })
      if (!res.ok) throw new Error(String(res.status))
      const body = (await res.json()) as ViesResponse
      return {
        status: body.isValid ? 'valid' : 'invalid',
        provider: 'vies', checked_at: new Date().toISOString(),
        name: body.name && body.name !== '---' ? body.name : undefined,
        consultation: body.requestIdentifier,   // reverse-charge evidence — keep it
      }
    } catch {
      // VIES down or CORS-blocked (browser) → honest fallback to the format floor
      const fb = checkVatFormat(cc, digits)
      return { ...fb, provider: 'vies→builtin', note: 'VIES unreachable — format check only (route via the server worker in production)' }
    }
  }
  return checkVatFormat(cc, digits)
}

/* ── company search ── */
export interface CompanyHit {
  name: string
  number?: string
  vat?: string
  country: string
  street?: string
  postal?: string
  city?: string
  website?: string
  source: string
}

/** The offline demo registry — deterministic for demos + e2e. */
const BUILTIN_BOOK: CompanyHit[] = [
  { name: 'Moonbase BV', vat: 'BE1039343221', country: 'BE', street: 'Frankrijklei 5', postal: '2000', city: 'Antwerpen', website: 'moonbase.be', source: 'builtin' },
  { name: 'Aurora Analytics SAS', number: '901234567', vat: 'FR12901234567', country: 'FR', street: 'Rue de Rivoli 12', postal: '75001', city: 'Paris', website: 'aurora-analytics.fr', source: 'builtin' },
  { name: 'Fjordline Logistics AS', number: '923609016', country: 'NO', street: 'Bryggen 7', postal: '5003', city: 'Bergen', website: 'fjordline-logistics.no', source: 'builtin' },
  { name: 'Delta Foods NV', vat: 'BE0402726875', country: 'BE', street: 'Grote Markt 1', postal: '2000', city: 'Antwerpen', source: 'builtin' },
  { name: 'Windmill Works BV', vat: 'NL812345678B01', country: 'NL', street: 'Weena 505', postal: '3013 AL', city: 'Rotterdam', source: 'builtin' },
]

interface BrregHit { navn: string; organisasjonsnummer: string; hjemmeside?: string; forretningsadresse?: { adresse?: string[]; postnummer?: string; poststed?: string } }
interface FrHit { nom_complet: string; siren: string; siege?: { adresse?: string; code_postal?: string; libelle_commune?: string } }

export async function searchCompany(q: string, s: CompanySettings = state): Promise<CompanyHit[]> {
  const query = q.trim()
  if (query.length < 3) return []
  const jobs: Promise<CompanyHit[]>[] = []
  if (s.registries.builtin) {
    const t = query.toLowerCase()
    jobs.push(Promise.resolve(BUILTIN_BOOK.filter((c) => c.name.toLowerCase().includes(t))))
  }
  if (s.registries.brreg) {
    jobs.push(fetch(`https://data.brreg.no/enhetsregisteret/api/enheter?navn=${encodeURIComponent(query)}&size=4`)
      .then((r) => r.json())
      .then((b: { _embedded?: { enheter?: BrregHit[] } }) => (b._embedded?.enheter ?? []).map((h) => ({
        name: h.navn, number: h.organisasjonsnummer, country: 'NO',
        street: h.forretningsadresse?.adresse?.[0], postal: h.forretningsadresse?.postnummer,
        city: h.forretningsadresse?.poststed, website: h.hjemmeside, source: 'brreg.no',
      })))
      .catch(() => []))
  }
  if (s.registries.fr) {
    jobs.push(fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(query)}&per_page=4`)
      .then((r) => r.json())
      .then((b: { results?: FrHit[] }) => (b.results ?? []).map((h) => ({
        name: h.nom_complet, number: h.siren, country: 'FR',
        street: h.siege?.adresse, postal: h.siege?.code_postal, city: h.siege?.libelle_commune,
        source: 'recherche-entreprises',
      })))
      .catch(() => []))
  }
  // uk: Companies House requires a key AND has no browser CORS — server worker territory.
  const all = (await Promise.all(jobs)).flat()
  return all.slice(0, 8)
}
